import { defineStore } from 'pinia';
import useStore from '@/store/index';
import { ref } from 'vue';
import { type ILearningObjectData, LearningObject, LearningObjectFactory, LearningObjectStates } from '@/entities';
import type { LearningObjectsRequestParams } from '@/services';

type LearningObjectsParams = {
  page?: number;
  pageSize?: number;
  tag?: string;
  query?: string;
  loTypes?: string[];
  learnerState?: string[];
  sort?: string;
};

type ServiceFetchFunction = (
  params: LearningObjectsRequestParams
) => Promise<{ data: ILearningObjectData[]; cursor?: string; total?: number }>;

type FetchFunction = {
  fetchFunction: ServiceFetchFunction;
};

export function createLearningObjectStore(id: string) {
  return defineStore(id, () => {
    const rootStore = useStore();
    const learningObjects = ref<LearningObject[]>([]);
    const learningObject = ref<LearningObject | undefined>();
    const currentPage = ref(1);
    const pageCursor = ref<string | undefined>();
    const totalCount = ref(0);
    const isLoading = ref(false);
    const currentSort = ref('featured');
    const featuredSortCache = ref<ILearningObjectData[]>([]);
    const FEATURED_PAGE_SIZE = 9;

    async function fetchAllAndSortFeatured(
      fetchFn: ServiceFetchFunction,
      baseParams: Omit<LearningObjectsRequestParams, 'page' | 'cursor' | 'sort'>,
      page: number
    ) {
      currentPage.value = page;
      currentSort.value = 'featured';

      if (featuredSortCache.value.length > 0) {
        const start = (page - 1) * FEATURED_PAGE_SIZE;
        learningObjects.value = featuredSortCache.value
          .slice(start, start + FEATURED_PAGE_SIZE)
          .map((lo) => new LearningObjectFactory().createLearningObject(lo));
        return;
      }

      isLoading.value = true;
      featuredSortCache.value = [];

      const allData: ILearningObjectData[] = [];
      let cursor: string | undefined = undefined;
      let pageNum = 1;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, cursor: nextCursor } = await fetchFn({ ...baseParams, page: pageNum, cursor, sort: 'name' });
        allData.push(...data);
        if (!nextCursor) break;
        cursor = nextCursor;
        pageNum++;
      }

      allData.sort((a, b) => {
        const aFeatured = a.tags?.includes('Featured') ?? false;
        const bFeatured = b.tags?.includes('Featured') ?? false;
        if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      featuredSortCache.value = allData;
      totalCount.value = allData.length;
      learningObjects.value = allData
        .slice(0, FEATURED_PAGE_SIZE)
        .map((lo) => new LearningObjectFactory().createLearningObject(lo));
      isLoading.value = false;
    }

    async function fetchLearningObjects({
      fetchFunction,
      page = 1,
      pageSize,
      sort,
      loTypes,
      learnerState,
      query,
      tag
    }: LearningObjectsParams & FetchFunction) {
      currentPage.value = page;
      isLoading.value = true;

      if (sort) {
        currentSort.value = sort;
      }

      if (page === 1) {
        pageCursor.value = undefined;
      }

      const { data, cursor, total } = await fetchFunction({
        page,
        cursor: pageCursor.value,
        pageSize,
        sort: currentSort.value,
        loTypes,
        learnerState,
        query,
        tag
      });

      if (cursor) {
        pageCursor.value = cursor;
      }

      totalCount.value = total ? total : data.length;
      learningObjects.value = data.map((learningObjectData) =>
        new LearningObjectFactory().createLearningObject(learningObjectData)
      );

      isLoading.value = false;
    }

    return {
      learningObjects,
      learningObject,
      currentPage,
      totalCount,
      isLoading,
      currentSort,

      async init() {},

      async fetchLearningObjectById(id: string) {
        isLoading.value = true;
        const learningObjectData = await rootStore.primeService.learningObject.fetchLearningObjectById(id);

        if (learningObjectData) {
          learningObject.value = new LearningObjectFactory().createLearningObject(learningObjectData);
        }
        isLoading.value = false;
      },

      async getLearningObjectById(id: string) {
        const learningObjectData = await rootStore.primeService.learningObject.fetchLearningObjectById(id);

        if (learningObjectData) {
          return new LearningObjectFactory().createLearningObject(learningObjectData);
        }

        return Promise.reject(new Error('Learning object not found'));
      },

      async getHighlightedLearningObjects({ page = 1 }: LearningObjectsParams) {
        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.fetchHighlightedLearningObjects.bind(
            rootStore.primeService.learningObject
          ),
          page
        });
      },

      async getInProgressLearningObjects({ page = 1 }: LearningObjectsParams) {
        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.fetchInProgressLearningObjects.bind(
            rootStore.primeService.learningObject
          ),
          page
        });
      },

      async getCompletedLearningObjects({ page = 1, pageSize }: LearningObjectsParams) {
        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.fetchCompletedLearningObjects.bind(
            rootStore.primeService.learningObject
          ),
          page,
          pageSize
        });
      },

      async getLearningObjectsByTag({ tag, page = 1, sort }: LearningObjectsParams) {
        if (sort === 'featured' || (!sort && currentSort.value === 'featured')) {
          await fetchAllAndSortFeatured(
            rootStore.primeService.learningObject.fetchLearningObjectsByTag.bind(rootStore.primeService.learningObject),
            { tag },
            page
          );
          return;
        }

        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.fetchLearningObjectsByTag.bind(
            rootStore.primeService.learningObject
          ),
          page,
          tag,
          sort
        });
      },

      async getLearningPrograms({ page = 1, sort }: LearningObjectsParams) {
        if (sort === 'featured' || (!sort && currentSort.value === 'featured')) {
          await fetchAllAndSortFeatured(
            rootStore.primeService.learningObject.fetchLearningPrograms.bind(rootStore.primeService.learningObject),
            {},
            page
          );
          return;
        }

        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.fetchLearningPrograms.bind(
            rootStore.primeService.learningObject
          ),
          page,
          sort
        });
      },

      async searchLearningObjects({ page = 1, query, loTypes, learnerState, sort }: LearningObjectsParams) {
        await fetchLearningObjects({
          fetchFunction: rootStore.primeService.learningObject.searchLearningObjects.bind(
            rootStore.primeService.learningObject
          ),
          query,
          loTypes,
          learnerState,
          page,
          sort
        });
      }
    };
  })();
}
